import * as React from 'react';

import styled from 'styled-components';

import { Translate } from 'react-localize-redux';

import { formatGenre } from '@webapp/utils/normalizers';
import { ReleaseGenre } from '@common/types/release/release-genre';

// Components
import Flex from '@components/layout/flex';
import AvatarPreview from './avatar-preview';
import SongIcon from '@components/basics/icons/song';
import Typography from '@components/basics/typography';
import SquareImg from '@components/basics/artwork-img';

export interface PreviewBoxProps {
  className?: string;
  srcImg?: string;
  songName: string;
  date: string;
  label: string;
  stores: string;
  preorderDate?: string;
  price?: number;
  artists: { name: string; avatar: string }[];
  featuring?: { name: string; avatar: string }[];
  genre: ReleaseGenre[];
  isMobile?: boolean;
  storesLength: number;
  regionsLength: number;
  hasYoutubeContentID: boolean;
  isStoreMaximizer: boolean;
}

export const PreviewBox: React.FC<PreviewBoxProps> = ({
  className,
  srcImg,
  songName,
  date,
  label,
  preorderDate,
  artists,
  featuring,
  genre,
  isMobile,
  storesLength,
  regionsLength,
  hasYoutubeContentID,
  isStoreMaximizer,
}) => {
  const mergedArtist = () => {
    return artists.concat(featuring ?? []);
  };

  return (
    <SectionWrapper className={`${className} ${isMobile ? 'mobile' : ''}`}>
      <Info>
        {srcImg ? (
          <SquareImg className="image-artwork" srcImg={srcImg} />
        ) : (
          <div className="icon-artwork">
            <SongIcon />
          </div>
        )}
        <div className="data-text">
          <div>
            <Typography
              className="song-name"
              type="subheader"
              customFont="semi-bold"
              align={isMobile ? 'center' : 'left'}
            >
              {songName}
            </Typography>

            <Typography className="genre" type="small">
              {formatGenre(genre)}
            </Typography>

            <div className="avatar-wrapper">
              <AvatarPreview
                items={mergedArtist()}
                avatarSize="24"
                breakingIndex={mergedArtist().length}
              />
              <Typography weight="700">
                {mergedArtist()
                  ?.map((option) => option.name)
                  .join(', ')?.length > 50
                  ? mergedArtist()
                      ?.map((option) => option.name)
                      .join(', ')
                      ?.slice(0, 50) + '...'
                  : mergedArtist()
                      ?.map((option) => option.name)
                      .join(', ')}
              </Typography>
            </div>
          </div>
          <Flex className="dates" justifyContent="space-between">
            {preorderDate ? (
              <div>
                <Typography className="date-title" type="small">
                  <Translate id="RELEASE.PREVIEW.DATES.PRE.RELEASE" />
                </Typography>
                <Typography weight="700">{preorderDate}</Typography>
              </div>
            ) : null}
            <div>
              <Typography className="date-title" type="small">
                <Translate
                  id="RELEASE.PREVIEW.DATES.RELEASE"
                  data={{ date: date }}
                />
              </Typography>
              <Typography weight="700">{date}</Typography>
            </div>
          </Flex>
        </div>
      </Info>

      <div className="boxes">
        <Flex className="box label-container" alignItems="center">
          <Typography className="label-circle" type="circle-black">
            C
          </Typography>
          <Typography className="label-circle" type="circle-black">
            P
          </Typography>
          <Typography className="label-name" weight="700">
            {label}
          </Typography>
        </Flex>

        <div className="box distribution">
          <Typography className="distribution-title" weight="700">
            <Translate id="RELEASE.PREVIEW.DISTRIBUTION.TITLE" />
          </Typography>
          <Typography className="distribution-text" type="small">
            <Translate
              id="RELEASE.PREVIEW.DISTRIBUTION.STORES"
              data={{ amount: storesLength }}
            />
            ,{' '}
            {isStoreMaximizer && (
              <>
                <Translate id="RELEASE.PREVIEW.DISTRIBUTION.STORE_PLUS" />,{' '}
              </>
            )}
            <Translate
              id="RELEASE.PREVIEW.DISTRIBUTION.COUNTRIES"
              data={{ amount: regionsLength }}
            />
            {hasYoutubeContentID && (
              <>
                , <Translate id="RELEASE.PREVIEW.DISTRIBUTION.YOUTUBE" />
              </>
            )}
          </Typography>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default PreviewBox;

const SectionWrapper = styled.div`
  padding: 20px;
  width: 100%;
  border-radius: 10px 10px 0px 0px;
  background: ${({ theme }) => theme.colors.background};

  @media ${({ theme }) => theme.device.tablet} {
    padding: 30px;
  }

  @media ${({ theme }) => theme.device.laptopL} {
    border-radius: 10px;
  }

  .image-artwork {
    width: 184px;
    height: 184px;
  }

  .icon-artwork {
    width: 184px;
    height: 184px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.colors.differentBackground};

    svg {
      width: 64px;
      height: 64px;
    }
  }

  .genre {
    margin-top: 5px;
    margin-bottom: 20px;
    text-align: center;

    @media ${({ theme }) => theme.device.tablet} {
      text-align: left;
      margin-bottom: 10px;
    }
  }

  .avatar-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .dates {
    margin-top: 20px;

    .date-title {
      color: ${({ theme }) => theme.colors.tertiary};
    }
  }

  .boxes {
    display: grid;
    grid-gap: 3px;
    margin-top: 20px;

    @media ${({ theme }) => theme.device.tablet} {
      margin-top: 30px;
    }
  }

  .box {
    padding: 20px;
    background-color: ${({ theme }) => theme.colors.differentBackground};
  }

  .label-container {
    border-radius: 4px 4px 0 0;

    .label-circle {
      margin-right: 5px;
      &:last-child {
        margin-right: 0;
      }
    }

    .label-name {
      margin-left: 10px;
    }
  }

  .distribution {
    border-radius: 0 0 4px 4px;

    .distribution-text {
      margin-top: 5px;
    }
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;

  @media ${({ theme }) => theme.device.tablet} {
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    gap: 30px;
  }

  .data-text {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 184px;
    max-width: calc(100% - 184px - 20px);
    @media ${({ theme }) => theme.device.tablet} {
      max-width: calc(100% - 184px - 30px);
    }

    .song-name {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`;
